import { FormControl, GridItem, Stack } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import gql from 'graphql-tag';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import { ConfigPowerSettingsSection_ConfigFragment } from './__generated__/ConfigPowerSettingsSection.graphql';
import { ConfigDisplayAutoRestart } from './ConfigDisplayAutoRestart';
import { ConfigDisplayBatteryManagement } from './ConfigDisplayBatteryManagement';
import { ConfigDisplaySignalDetectionToggle } from './ConfigDisplaySignalDetection';

interface Props {
  config: ConfigPowerSettingsSection_ConfigFragment;
  isNotSupported: boolean;
  configStatus?: ConfigurationStatusType;
}

export function ConfigPowerSettingsSection({ config, isNotSupported, configStatus }: Props) {
  const { verifyUserPermissions } = useAuth();

  const isConfigUpdateDisabled =
    checkStateToDisableTab(configStatus) ||
    !verifyUserPermissions([Permission.ConfigurationUpdate]);
  const { t } = useTranslation();

  function showAutoRestart() {
    const hasAutoRestartTrait = config?.autoRestart;
    return hasAutoRestartTrait;
  }

  return (
    <GridItem colSpan={{ base: 8, md: 7 }}>
      {isNotSupported ? (
        <>{t('powerNotSupported')}</>
      ) : (
        <Stack direction="column" spacing="10">
          {!isNil(config.signalDetection?.reported) && (
            <FormControl>
              <ConfigDisplaySignalDetectionToggle
                config={config}
                isDisabled={isConfigUpdateDisabled}
              />
            </FormControl>
          )}
          {showAutoRestart() && (
            <ConfigDisplayAutoRestart config={config} configStatus={configStatus} />
          )}
          {!isNil(config.battery?.reported) && (
            <ConfigDisplayBatteryManagement config={config} isDisabled={isConfigUpdateDisabled} />
          )}
        </Stack>
      )}
    </GridItem>
  );
}

ConfigPowerSettingsSection.graphql = {
  fragments: {
    ConfigPowerSettingsSection_config: gql`
      fragment ConfigPowerSettingsSection_config on ConfigurationModel {
        id
        deviceSdk {
          sdk
          version
        }
        power {
          reportedAt
        }
        autoRestart {
          reported {
            switch
            time
          }
        }
        ...ConfigDisplaySignalDetectionToggle_config
        ...useConfigPowerToggle_config
        ...ConfigDisplayBatteryManagement_config
      }
    `,
  },
};
